import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import {
  useAgentSurveyResponses,
  useSurveyResponseDetail,
  useSurveyLeadOwners,
  useCreateLeadFromResponse
} from '../hooks/useAgentSurveyResponses'
import { TypeBadge } from '../components/Marketing/marketingFormsUi'
import LeadDetailModal from '../components/Leads/LeadDetailModal'

const { FiClipboard, FiChevronDown, FiChevronUp, FiX } = FiIcons

const formatDateTime = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

const formatStatusLabel = (status) => {
  if (!status) return '—'
  if (status === '1_1_completed') return '1-1 Completed'
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ')
}

const getLeadOwner = (response) => {
  const lead = response.leads
  if (!lead) return null
  const owner = lead.users
  if (Array.isArray(owner)) return owner[0] ?? null
  return owner ?? null
}

const getDisplayEmail = (response) => {
  if (response.lead_id && response.leads?.email) return response.leads.email
  return response.respondent_email || '—'
}

const getDisplayPhone = (response) => {
  if (response.lead_id && response.leads?.phone) return response.leads.phone
  return response.respondent_phone || '—'
}

const getLeadOwnerName = (response) => {
  if (!response.lead_id) return '—'
  const owner = getLeadOwner(response)
  return owner?.full_name?.trim() || '—'
}

const getLinkedLeadName = (response) =>
  response.leads?.full_name?.trim() || response.respondent_name?.trim() || 'Lead'

const useEscapeClose = (open, onClose) => {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
}

const ModalShell = ({ open, onClose, title, children, maxWidth = 'max-w-lg' }) => {
  useEscapeClose(open, onClose)
  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`bg-white rounded-2xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </motion.div>
    </div>
  )
}

const LeadHoverTooltip = ({ lead, owner, children }) => {
  const [visible, setVisible] = useState(false)
  const timerRef = useRef(null)

  const show = () => {
    timerRef.current = window.setTimeout(() => setVisible(true), 200)
  }

  const hide = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setVisible(false)
  }

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  if (!lead) return children

  return (
    <span className="relative inline-block" onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {visible && (
        <div
          role="tooltip"
          className="absolute z-30 bottom-full left-0 mb-2 w-64 p-3 text-xs bg-white border border-gray-200 rounded-xl shadow-lg pointer-events-none"
        >
          <p className="font-semibold text-slate-800 mb-2">{lead.full_name || '—'}</p>
          <dl className="space-y-1 text-slate-600">
            <div>
              <span className="text-slate-400">Email: </span>
              {lead.email || '—'}
            </div>
            <div>
              <span className="text-slate-400">Phone: </span>
              {lead.phone || '—'}
            </div>
            <div>
              <span className="text-slate-400">Status: </span>
              {formatStatusLabel(lead.status)}
            </div>
            <div>
              <span className="text-slate-400">Payment: </span>
              {lead.payment_status || '—'}
            </div>
            <div>
              <span className="text-slate-400">Owner: </span>
              {owner?.full_name || '—'}
            </div>
            <div>
              <span className="text-slate-400">Created: </span>
              {formatDateTime(lead.created_at)}
            </div>
          </dl>
        </div>
      )}
    </span>
  )
}

const CreateLeadModal = ({
  open,
  onClose,
  response,
  owners,
  ownersLoading,
  defaultOwnerId,
  onSubmit,
  submitting
}) => {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [ownerSearch, setOwnerSearch] = useState('')
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false)

  useEffect(() => {
    if (!open || !response) return
    setFullName(response.respondent_name?.trim() || '')
    setEmail(response.respondent_email?.trim() || '')
    setPhone(response.respondent_phone?.trim() || '')
    setOwnerId(defaultOwnerId || '')
    setOwnerSearch('')
    setOwnerDropdownOpen(false)
  }, [open, response, defaultOwnerId])

  const filteredOwners = useMemo(() => {
    const q = ownerSearch.trim().toLowerCase()
    if (!q) return owners
    return owners.filter((u) => (u.full_name || u.email || '').toLowerCase().includes(q))
  }, [owners, ownerSearch])

  const selectedOwner = owners.find((u) => u.id === ownerId)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!fullName.trim()) {
      toast.error('Full name is required')
      return
    }
    if (!ownerId) {
      toast.error('Lead owner is required')
      return
    }
    onSubmit({
      full_name: fullName.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      agent_id: ownerId,
      status: 'new',
      event_id: response?.event_id ?? null
    })
  }

  return (
    <ModalShell open={open} onClose={onClose} title="Create lead from submission">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Full name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#00AAFF]"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#00AAFF]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#00AAFF]"
          />
        </div>
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Lead owner <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={ownerDropdownOpen ? ownerSearch : selectedOwner?.full_name || ''}
            onChange={(e) => {
              setOwnerSearch(e.target.value)
              setOwnerDropdownOpen(true)
            }}
            onFocus={() => setOwnerDropdownOpen(true)}
            placeholder={ownersLoading ? 'Loading…' : 'Search by name'}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#00AAFF]"
            autoComplete="off"
            required={!ownerId}
          />
          {ownerDropdownOpen && (
            <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg">
              {ownersLoading ? (
                <li className="px-4 py-2 text-sm text-slate-500">Loading users…</li>
              ) : filteredOwners.length === 0 ? (
                <li className="px-4 py-2 text-sm text-slate-500">No users found</li>
              ) : (
                filteredOwners.map((user) => (
                  <li key={user.id}>
                    <button
                      type="button"
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${
                        user.id === ownerId ? 'bg-sky-50 text-[#00AAFF]' : 'text-slate-700'
                      }`}
                      onClick={() => {
                        setOwnerId(user.id)
                        setOwnerSearch('')
                        setOwnerDropdownOpen(false)
                      }}
                    >
                      {user.full_name?.trim() || user.email || user.id}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
          <input
            type="text"
            value="new"
            readOnly
            className="w-full px-4 py-2.5 border border-gray-100 rounded-xl text-sm bg-slate-50 text-slate-600"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-[#00AAFF] rounded-xl hover:bg-[#0099e6] disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create lead'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

const formatAnswer = (answer) => {
  if (!answer) return '—'
  if (answer.answer_text) return answer.answer_text
  const opt = answer.marketing_form_options
  if (opt?.option_text_es) return opt.option_text_es
  return '—'
}

const AgentResponseRow = ({
  response,
  auth,
  onOpenLeadDetail,
  onOpenCreateLead
}) => {
  const [expanded, setExpanded] = useState(false)
  const form = response.marketing_forms
  const isQuiz = form?.type === 'quiz'
  const lead = response.leads
  const owner = getLeadOwner(response)
  const isLinked = Boolean(response.lead_id && lead)

  const { data: detail, isLoading } = useSurveyResponseDetail(
    expanded ? response.id : null,
    auth,
    { enabled: expanded }
  )

  return (
    <>
      <tr
        className="border-b border-gray-50 hover:bg-slate-50/50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="p-4 text-slate-400">
          <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
        </td>
        <td className="p-4 font-medium text-slate-800">{form?.title || '—'}</td>
        <td className="p-4">{form?.type ? <TypeBadge type={form.type} /> : '—'}</td>
        <td className="p-4">{response.respondent_name || '—'}</td>
        <td className="p-4 text-slate-600">{getDisplayEmail(response)}</td>
        <td className="p-4 text-slate-600">{getDisplayPhone(response)}</td>
        <td className="p-4 text-slate-600">{getLeadOwnerName(response)}</td>
        <td className="p-4 text-slate-500">
          {response.submitted_at ? new Date(response.submitted_at).toLocaleString() : '—'}
        </td>
        <td className="p-4">{isQuiz ? (response.score ?? '—') : '—'}</td>
        <td className="p-4" onClick={(e) => e.stopPropagation()}>
          {isLinked ? (
            <LeadHoverTooltip lead={lead} owner={owner}>
              <button
                type="button"
                onClick={() => onOpenLeadDetail(response)}
                className="text-[#00AAFF] hover:underline font-medium text-left"
              >
                {getLinkedLeadName(response)}
              </button>
            </LeadHoverTooltip>
          ) : (
            <button
              type="button"
              onClick={() => onOpenCreateLead(response)}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-[#00AAFF] border border-[#00AAFF]/30 rounded-lg hover:bg-sky-50"
            >
              Create Lead
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={10} className="p-4 bg-slate-50/80">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-[#00AAFF] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <ul className="space-y-2">
                {(detail?.marketing_form_answers || []).map((a) => (
                  <li key={a.id} className="text-sm">
                    <span className="font-medium text-slate-700">
                      {a.marketing_form_questions?.question_text_es || 'Question'}:
                    </span>{' '}
                    <span className="text-slate-600">{formatAnswer(a)}</span>
                  </li>
                ))}
                {(detail?.marketing_form_answers || []).length === 0 && (
                  <li className="text-sm text-slate-500">No answers recorded.</li>
                )}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

const AgentSurveyResponses = () => {
  const { role, profile, user } = useAuth()
  const agentId = profile?.id ?? user?.id
  const auth = { role: profile?.role ?? role, userId: agentId }

  const [typeFilter, setTypeFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [leadDetailLeadId, setLeadDetailLeadId] = useState(null)
  const [createLeadResponse, setCreateLeadResponse] = useState(null)

  const { data: responses = [], isLoading, error } = useAgentSurveyResponses(agentId, auth)
  const { data: owners = [], isLoading: ownersLoading } = useSurveyLeadOwners(auth, {
    enabled: Boolean(createLeadResponse)
  })
  const createLeadMutation = useCreateLeadFromResponse(agentId, auth)

  const filteredResponses = useMemo(() => {
    let list = [...responses]
    if (typeFilter) {
      list = list.filter((r) => r.marketing_forms?.type === typeFilter)
    }
    if (dateFrom) {
      const from = new Date(dateFrom)
      list = list.filter((r) => r.submitted_at && new Date(r.submitted_at) >= from)
    }
    if (dateTo) {
      const to = new Date(dateTo)
      to.setHours(23, 59, 59, 999)
      list = list.filter((r) => r.submitted_at && new Date(r.submitted_at) <= to)
    }
    return list
  }, [responses, typeFilter, dateFrom, dateTo])

  useEffect(() => {
    if (error) toast.error(error.message ?? 'Failed to load survey responses')
  }, [error])

  const handleCreateLead = useCallback(
    async (leadData) => {
      if (!createLeadResponse?.id) return
      try {
        await createLeadMutation.mutateAsync({
          responseId: createLeadResponse.id,
          leadData
        })
        toast.success('Lead created and linked to submission')
        setCreateLeadResponse(null)
      } catch (err) {
        toast.error(err?.message ?? 'Failed to create lead')
      }
    },
    [createLeadMutation, createLeadResponse]
  )

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center gap-3">
        <SafeIcon icon={FiClipboard} className="w-7 h-7 text-[#00AAFF]" />
        <h1 className="text-2xl font-bold text-slate-800">Survey Responses</h1>
      </div>
      <p className="text-sm text-slate-500">
        Read-only view of form, survey, and quiz submissions from your leads.
      </p>

      <div className="flex flex-wrap gap-3 bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#00AAFF]"
        >
          <option value="">All types</option>
          <option value="form">Form</option>
          <option value="survey">Survey</option>
          <option value="quiz">Quiz</option>
        </select>
        <div>
          <label className="block text-xs text-slate-500 mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-xl text-sm"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-2 border-[#00AAFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredResponses.length === 0 ? (
          <div className="text-center py-16 px-6">
            <SafeIcon icon={FiClipboard} className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">No survey responses yet</p>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              Responses from your leads will appear here once they complete a form.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-gray-100">
                <tr className="text-left text-slate-600">
                  <th className="p-4 w-8" />
                  <th className="p-4 font-medium">Form Title</th>
                  <th className="p-4 font-medium">Type</th>
                  <th className="p-4 font-medium">Respondent Name</th>
                  <th className="p-4 font-medium">Email</th>
                  <th className="p-4 font-medium">Phone</th>
                  <th className="p-4 font-medium">Lead Owner</th>
                  <th className="p-4 font-medium">Submitted At</th>
                  <th className="p-4 font-medium">Score</th>
                  <th className="p-4 font-medium">Linked Lead</th>
                </tr>
              </thead>
              <tbody>
                {filteredResponses.map((response) => (
                  <AgentResponseRow
                    key={response.id}
                    response={response}
                    auth={auth}
                    onOpenLeadDetail={(response) => setLeadDetailLeadId(response.lead_id)}
                    onOpenCreateLead={setCreateLeadResponse}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LeadDetailModal
        leadId={leadDetailLeadId}
        isOpen={Boolean(leadDetailLeadId)}
        onClose={() => setLeadDetailLeadId(null)}
      />

      <CreateLeadModal
        open={Boolean(createLeadResponse)}
        onClose={() => setCreateLeadResponse(null)}
        response={createLeadResponse}
        owners={owners}
        ownersLoading={ownersLoading}
        defaultOwnerId={agentId}
        onSubmit={handleCreateLead}
        submitting={createLeadMutation.isPending}
      />
    </motion.div>
  )
}

export default AgentSurveyResponses
